import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import {
  PlusIcon,
  SearchIcon,
  FilterIcon,
  OfficeBuildingIcon,
  CalendarIcon,
  UserIcon,
  ChevronRightIcon,
  DotsVerticalIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
} from "@heroicons/react/outline";
import { projectsAPI } from "../api/projects";
import { useAuth } from "../context/AuthContext";
import LoadingSpinner from "../components/common/LoadingSpinner";
import ProjectForm from "../components/projects/ProjectForm";
import toast from "react-hot-toast";

const ProjectsPage = () => {
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showMenu, setShowMenu] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const queryClient = useQueryClient();

  const canManage =
    user?.role === "super_admin" || user?.role === "project_manager";

  // Fetch projects - API handles role-based filtering
  const { data, isLoading, error } = useQuery({
    queryKey: ["projects", statusFilter, searchTerm],
    queryFn: () =>
      projectsAPI.getAll({
        status: statusFilter !== "all" ? statusFilter : undefined,
        search: searchTerm || undefined,
      }),
    staleTime: 5 * 60 * 1000,
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => projectsAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries(["projects"]);
      toast.success("Project deleted successfully");
    },
    onError: () => toast.error("Failed to delete project"),
  });

  const projects = data?.data?.projects || [];
  const pagination = data?.data?.pagination || {};

  const getStatusColor = (status) => {
    const colors = {
      active: "badge-success",
      planning: "badge-info",
      on_hold: "badge-warning",
      completed: "badge-primary",
      cancelled: "badge-danger",
    };
    return colors[status] || "badge-info";
  };

  const getStatusIcon = (status) => {
    const icons = {
      active: "🟢",
      planning: "🔵",
      on_hold: "🟡",
      completed: "✅",
      cancelled: "❌",
    };
    return icons[status] || "🔵";
  };

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this project?")) {
      deleteMutation.mutate(id);
    }
  };

  if (isLoading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
            {user?.role === "sub_manager" || user?.role === "foreman"
              ? "My Projects"
              : "Projects"}
          </h1>
          <p className="text-slate-600 dark:text-slate-400">
            {user?.role === "sub_manager" || user?.role === "foreman"
              ? "Projects assigned to you"
              : "Manage all your construction projects"}
          </p>
        </div>
        {canManage && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn-primary flex items-center gap-2"
          >
            <PlusIcon className="h-5 w-5" />
            New Project
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1 relative">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
          <input
            type="text"
            placeholder="Search projects..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500"
          />
        </div>
        <div className="flex items-center gap-2">
          <FilterIcon className="h-5 w-5 text-slate-400" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500"
          >
            <option value="all">All Status</option>
            <option value="planning">Planning</option>
            <option value="active">Active</option>
            <option value="on_hold">On Hold</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      {/* Projects Grid */}
      {projects.length === 0 ? (
        <div className="text-center py-12">
          <OfficeBuildingIcon className="h-16 w-16 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
            No projects found
          </h3>
          <p className="text-slate-600 dark:text-slate-400">
            {user?.role === "sub_manager" || user?.role === "foreman"
              ? "You haven't been assigned to any projects yet"
              : "Create your first project to get started"}
          </p>
          {canManage && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="btn-primary mt-4 inline-flex items-center gap-2"
            >
              <PlusIcon className="h-5 w-5" />
              Create Project
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {projects.map((project, index) => (
            <motion.div
              key={project.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="card hover:shadow-lg transition-all duration-300 group"
            >
              {/* ... rest of project card ... */}
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">
                      {getStatusIcon(project.status)}
                    </span>
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-white truncate">
                      {project.name}
                    </h3>
                  </div>
                  {/* Show role badge for Sub Managers/Foremen */}
                  {project.my_role && (
                    <span
                      className={`badge ${
                        project.my_role === "project_manager"
                          ? "badge-primary"
                          : project.my_role === "sub_manager"
                            ? "badge-info"
                            : project.my_role === "foreman"
                              ? "badge-warning"
                              : "badge-success"
                      } text-xs`}
                    >
                      {project.my_role.replace("_", " ").toUpperCase()}
                    </span>
                  )}
                  <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 line-clamp-2">
                    {project.description || "No description"}
                  </p>
                </div>
                <div className="relative">
                  <button
                    onClick={() =>
                      setShowMenu(showMenu === project.id ? null : project.id)
                    }
                    className="p-1 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                  >
                    <DotsVerticalIcon className="h-5 w-5 text-slate-400" />
                  </button>

                  {showMenu === project.id && (
                    <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-slate-200 dark:border-slate-700 py-1 z-10">
                      <Link
                        to={`/projects/${project.id}`}
                        className="flex items-center gap-2 px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700"
                        onClick={() => setShowMenu(null)}
                      >
                        <EyeIcon className="h-4 w-4" />
                        View Details
                      </Link>
                      <button
                        onClick={() => {
                          setShowMenu(null);
                          setEditingProject(project);
                        }}
                        className="flex items-center gap-2 w-full px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700"
                      >
                        <PencilIcon className="h-4 w-4" />
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(project.id)}
                        className="flex items-center gap-2 w-full px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                      >
                        <TrashIcon className="h-4 w-4" />
                        Delete
                      </button>
                    </div>
                  )}
                  {editingProject && (
                    <ProjectFormModal
                      project={editingProject}
                      onClose={() => setEditingProject(null)}
                      onSuccess={() => {
                        queryClient.invalidateQueries(["projects"]);
                        setEditingProject(null);
                        toast.success("Project updated successfully");
                      }}
                    />
                  )}
                </div>
              </div>

              {/* Details */}
              <div className="mt-4 space-y-2">
                <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                  <UserIcon className="h-4 w-4" />
                  <span>{project.client_name || "No client"}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                  <CalendarIcon className="h-4 w-4" />
                  <span>
                    {project.start_date
                      ? new Date(project.start_date).toLocaleDateString()
                      : "Not started"}
                  </span>
                  <span>→</span>
                  <span>
                    {project.expected_end_date
                      ? new Date(project.expected_end_date).toLocaleDateString()
                      : "No end date"}
                  </span>
                </div>
              </div>

              {/* Progress */}
              <div className="mt-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium text-slate-700 dark:text-slate-300">
                    Progress
                  </span>
                  <span className="text-slate-600 dark:text-slate-400">
                    {project.current_spent && project.total_budget
                      ? Math.round(
                          (project.current_spent / project.total_budget) * 100,
                        )
                      : 0}
                    %
                  </span>
                </div>
                <div className="mt-1 w-full h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-primary-500 to-primary-600 rounded-full transition-all duration-500"
                    style={{
                      width: `${
                        project.current_spent && project.total_budget
                          ? Math.min(
                              (project.current_spent / project.total_budget) *
                                100,
                              100,
                            )
                          : 0
                      }%`,
                    }}
                  />
                </div>
              </div>

              {/* Status Badge */}
              <div className="mt-4 flex items-center justify-between">
                <span className={`badge ${getStatusColor(project.status)}`}>
                  {project.status || "Planning"}
                </span>
                <Link
                  to={`/projects/${project.id}`}
                  className="text-sm text-primary-500 hover:text-primary-600 dark:text-primary-400 dark:hover:text-primary-300 flex items-center gap-1"
                >
                  View Details
                  <ChevronRightIcon className="h-4 w-4" />
                </Link>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-between mt-6">
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Showing {projects.length} of {pagination.total} projects
          </p>
          <div className="flex gap-2">
            {/* Pagination buttons would go here */}
          </div>
        </div>
      )}

      {/* Create Project Modal */}
      {showCreateModal && (
        <ProjectFormModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            queryClient.invalidateQueries(["projects"]);
            setShowCreateModal(false);
          }}
        />
      )}
    </div>
  );
};

// Project Form Modal Component
const ProjectFormModal = ({ onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    client_name: "",
    client_phone: "",
    total_budget: "",
    start_date: "",
    expected_end_date: "",
    status: "planning",
    priority: "medium",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await projectsAPI.create(formData);
      toast.success("Project created successfully");
      onSuccess();
    } catch (error) {
      toast.error(
        error.response?.data?.error?.message || "Failed to create project",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="p-6 border-b border-slate-200 dark:border-slate-700">
          <h2 className="text-xl font-bold text-slate-900 dark:text-white">
            Create New Project
          </h2>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Fill in the details to create a new project
          </p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Project Name *
            </label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500"
              placeholder="Enter project name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              rows="3"
              className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500"
              placeholder="Project description"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Client Name
              </label>
              <input
                type="text"
                value={formData.client_name}
                onChange={(e) =>
                  setFormData({ ...formData, client_name: e.target.value })
                }
                className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                placeholder="Client name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Client Phone
              </label>
              <input
                type="text"
                value={formData.client_phone}
                onChange={(e) =>
                  setFormData({ ...formData, client_phone: e.target.value })
                }
                className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                placeholder="+251-911-000000"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Total Budget
              </label>
              <input
                type="number"
                value={formData.total_budget}
                onChange={(e) =>
                  setFormData({ ...formData, total_budget: e.target.value })
                }
                className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                placeholder="0.00"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Priority
              </label>
              <select
                value={formData.priority}
                onChange={(e) =>
                  setFormData({ ...formData, priority: e.target.value })
                }
                className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Start Date
              </label>
              <input
                type="date"
                value={formData.start_date}
                onChange={(e) =>
                  setFormData({ ...formData, start_date: e.target.value })
                }
                className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Expected End Date
              </label>
              <input
                type="date"
                value={formData.expected_end_date}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    expected_end_date: e.target.value,
                  })
                }
                className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Status
            </label>
            <select
              value={formData.status}
              onChange={(e) =>
                setFormData({ ...formData, status: e.target.value })
              }
              className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500"
            >
              <option value="planning">Planning</option>
              <option value="active">Active</option>
              <option value="on_hold">On Hold</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-200 dark:border-slate-700">
            <button type="button" onClick={onClose} className="btn-secondary">
              Cancel
            </button>
            <button type="submit" disabled={loading} className="btn-primary">
              {loading ? "Creating..." : "Create Project"}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default ProjectsPage;
